import { useState } from "react";
import { Check, Star, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PACKAGES, RECOMMENDED_PACKAGE, REGULAMIN_URL, PREMIERE_DATE, PRESALE_DEADLINE_LABEL } from "../config/site";
import { Countdown } from "./Countdown";
import { PromoCode } from "./PromoCode";
import { usePresaleCountdown } from "../lib/presale";

const fmt = (n) => n.toLocaleString("pl-PL");

const PackageCard = ({ pkg, highlighted, ended }) => {
    const savings = pkg.regularPrice - pkg.presalePrice;
    const price = ended ? pkg.regularPrice : pkg.presalePrice;
    const recommended = RECOMMENDED_PACKAGE === pkg.id;

    return (
        <article
            data-testid={`pricing-card-${pkg.id}`}
            className={`relative h-full rounded-3xl bg-white p-6 lg:p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                highlighted
                    ? "border-2 border-ink shadow-2xl lg:scale-[1.02]"
                    : "border border-border shadow-md hover:shadow-xl"
            }`}
        >
            {recommended && (
                <span
                    data-testid="pricing-recommended-badge"
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1.5 rounded-full bg-tang text-ink text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5"
                >
                    <Star className="w-3 h-3" fill="currentColor" />
                    Najczęściej wybierany
                </span>
            )}
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-ink-mute mt-2">
                {pkg.tagline}
            </p>
            <h3 className="font-display uppercase mt-2 text-2xl lg:text-3xl font-bold text-ink leading-tight">
                {pkg.name}
            </h3>

            <img
                src={pkg.mockup}
                alt={`Zawartość pakietu ${pkg.name} - kurs 100 rozwiązań i dodatkowe materiały EduNation`}
                loading="lazy"
                data-testid={`pricing-mockup-${pkg.id}`}
                className="my-5 w-full h-48 object-contain"
            />

            <ul className="space-y-2.5 flex-1">
                {pkg.features.map((f) => (
                    <li key={f} className="flex gap-3 items-start">
                        <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-ink flex items-center justify-center text-white">
                            <Check className="w-3 h-3" strokeWidth={3} />
                        </span>
                        <span className="text-sm text-ink leading-relaxed">{f}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-7 pt-6 border-t border-border">
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-mute mb-1.5">
                    {ended ? "Cena regularna" : "Cena Early Bird"}
                </p>
                <div className="flex items-end gap-3 flex-wrap">
                    <span
                        data-testid={`pricing-price-${pkg.id}`}
                        className="font-display text-5xl lg:text-6xl font-extrabold text-ink leading-none"
                    >
                        {fmt(price)}
                    </span>
                    <span className="font-display text-xl font-bold text-ink leading-none pb-1">
                        zł
                    </span>
                    {!ended && (
                        <span className="font-display text-2xl lg:text-3xl font-bold text-ink/45 line-through decoration-[3px] decoration-ink/40 leading-none pb-0.5">
                            {fmt(pkg.regularPrice)} zł
                        </span>
                    )}
                </div>
                {!ended && (
                    <p
                        data-testid={`pricing-savings-${pkg.id}`}
                        className="mt-3 inline-block rounded-full bg-tang px-3.5 py-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-ink"
                    >
                        Oszczędzasz {fmt(savings)} zł
                    </p>
                )}
                <p className="mt-1.5 text-[10px] text-ink/55 leading-relaxed">
                    Najniższa cena w ciągu ostatnich 30 dni wynosiła {fmt(pkg.lowestPrice30d)} zł
                    (cena w PLN brutto)
                </p>

                <a
                    data-testid={`pricing-select-button-${pkg.id}`}
                    href={pkg.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                        window.fbq?.("track", "InitiateCheckout", {
                            content_name: pkg.name,
                            value: price,
                            currency: "PLN",
                        })
                    }
                    className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[52px] text-sm transition-all duration-300 ${
                        highlighted
                            ? "bg-tang hover:bg-[#EFE238] text-ink shadow-md"
                            : "border-2 border-ink text-ink hover:bg-ink hover:text-white"
                    }`}
                >
                    {pkg.cta}
                    <ArrowRight className="w-4 h-4" />
                </a>
            </div>
        </article>
    );
};

export const Pricing = () => {
    const [selected, setSelected] = useState(RECOMMENDED_PACKAGE || PACKAGES[0].id);
    const { ended, lastDay, promoActive, promoTime } = usePresaleCountdown();

    return (
        <section id="pakiety" data-testid="pricing-section" className="py-20 sm:py-28 bg-cream-deep">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading
                    kicker="Pakiety i ceny"
                    title="Wybierz, ile wsparcia potrzebujesz"
                    lead="Możesz kupić sam kurs albo wybrać pakiet zawierający dodatkowe materiały i szkolenia dotyczące pracy z dorosłymi."
                />

                <Reveal delay={0.1}>
                    <div
                        data-testid="presale-banner"
                        className="mt-8 rounded-2xl bg-presale text-white px-6 py-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-7 gap-y-4 text-center"
                    >
                        {ended ? (
                            <>
                                <span className="font-display font-extrabold uppercase tracking-wide text-lg sm:text-xl leading-tight">
                                    Early Bird zakończony
                                </span>
                                <span className="text-sm font-semibold text-white/85">
                                    Sprawdź aktualne ceny pakietów poniżej
                                </span>
                            </>
                        ) : promoActive ? (
                            <>
                                <span className="font-display font-extrabold uppercase tracking-wide text-lg sm:text-2xl leading-tight">
                                    {promoTime.lastDay ? "Tylko dziś!" : "Tylko 27 października"}
                                    <span className="text-tang">
                                        {" "}-50 zł z kodem <PromoCode />
                                        {promoTime.lastDay && " · do północy"}
                                    </span>
                                </span>
                                <Countdown variant="blocks" target="promo" />
                                <span className="text-sm font-semibold text-white/85">
                                    Kod wpisujesz przy zakupie · ceny na kartach przed rabatem
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="font-display font-extrabold uppercase tracking-wide text-lg sm:text-xl leading-tight">
                                    {lastDay
                                        ? "Ostatni dzień Early Bird!"
                                        : "Early Bird kończy się"}
                                    <span className="text-tang">
                                        {" "}
                                        {lastDay ? "Koniec dziś o północy" : PRESALE_DEADLINE_LABEL}
                                    </span>
                                </span>
                                <Countdown variant="blocks" />
                                <span className="text-sm font-semibold text-white/85">
                                    Niższe ceny Early Bird · premiera kursu {PREMIERE_DATE}
                                </span>
                            </>
                        )}
                    </div>
                </Reveal>

                <div
                    data-testid="mobile-package-selector"
                    className="lg:hidden mt-10 grid grid-cols-3 gap-1.5 rounded-full bg-white border border-border p-1.5"
                    role="tablist"
                    aria-label="Wybierz pakiet"
                >
                    {PACKAGES.map((pkg) => (
                        <button
                            key={pkg.id}
                            role="tab"
                            aria-selected={selected === pkg.id}
                            data-testid={`mobile-package-tab-${pkg.id}`}
                            onClick={() => setSelected(pkg.id)}
                            className={`rounded-full px-2 py-3 min-h-[44px] text-[11px] sm:text-xs font-bold leading-tight transition-colors duration-300 ${
                                selected === pkg.id
                                    ? "bg-ink text-white shadow-md"
                                    : "text-ink-soft"
                            }`}
                        >
                            {pkg.selectorLabel}
                        </button>
                    ))}
                </div>

                <div className="mt-10 lg:mt-14 grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
                    {PACKAGES.map((pkg, i) => (
                        <Reveal key={pkg.id} delay={i * 0.1} className="h-full">
                            <PackageCard pkg={pkg} highlighted={selected === pkg.id} ended={ended} />
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15}>
                    <p
                        data-testid="pricing-legal-note"
                        className="mt-10 text-center text-sm text-ink-mute"
                    >
                        Wszystkie ceny są cenami brutto. Kupując dostęp, akceptujesz{" "}
                        <a
                            href={REGULAMIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-ink underline underline-offset-2"
                        >
                            Regulamin
                        </a>
                        .
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
